import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import {
  Autocomplete,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import React, { useEffect, useState } from "react";
import Loading from "../../Component/Loading";
import NormalTable from "../../Component/NormalTable";
import { color } from "../../StyleData/Config";
import { masterAPI } from "../Masters/dataConfig";
import { axiosPrivate } from "../../axios/axios";

const StockRoomReport = () => {
  const [stockOpen, setStockOpen] = useState(false);
  const [issueData, setIssueData] = useState([]);
  const [stockDateFrom, setStockDateFrom] = useState(
    moment().subtract(3, "months").format("YYYY-MM-DDTHH:mm:ss")
  );
  const [stockDateTo, setStockDateTo] = useState(
    moment().format("YYYY-MM-DDTHH:mm:ss")
  );
  const [isStockLoading, setIsStockLoading] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const [stockRoomOptions, setStockRoomOptions] = useState([]);
  const [partOptions, setPartOptions] = useState([]);
  const [stockRoomFilter, setStockRoomFilter] = useState([]);
  const [partFilter, setPartFilter] = useState([]);

  useEffect(() => {
    getStockData();
    getFilterOptions();
  }, []);

  const getFilterOptions = async () => {
    const stockRoomLink = masterAPI["selectList"].stockRoom;
    await axiosPrivate
      .get(stockRoomLink)
      .then((res) => setStockRoomOptions(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        console.log(e, "stock room options error");
        setStockRoomOptions([]);
      });

    const partLink = masterAPI["selectList"].partsInventory;
    await axiosPrivate
      .get(partLink)
      .then((res) => setPartOptions(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        console.log(e, "part options error");
        setPartOptions([]);
      });
  };

  const getStockData = async () => {
    setIsStockLoading(true);
    const chartGetLink = masterAPI["issue"].getallCompleteIssueRecord;
    const dateFromandTo = {
      createdDateFrom: stockDateFrom,
      createdDateTo: stockDateTo,
      stockRoomIds: stockRoomFilter.map((s) => s.value),
      partIds: partFilter.map((p) => p.value),
    };

    await axiosPrivate
      .post(`${chartGetLink}`, dateFromandTo)
      .then((res) => {
        const resData = res.data.result;
        setIssueData(resData);
      })
      .catch((e) => {
        console.log(e);
        setIssueData([]);
      });

    setIsStockLoading(false);
  };

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const columnsIssue = [
    {
      Header: "Work Order Code",
      accessor: "workOrderCode",
    },
    {
      Header: "Work Order Name",
      accessor: "workOrderName",
    },
    {
      Header: "Issued Date",
      accessor: "issuedDate",
      Cell: ({ cell }) => {
        return (
          <Typography sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
            {cell.value !== null && cell.value !== undefined
              ? moment(cell.value).format("DD/MM/YYYY")
              : "---"}
          </Typography>
        );
      },
    },
  ];

  return (
    <Grid container spacing={"1rem"}>
      <Grid item xs={12}>
        <Paper sx={{ padding: "1rem" }}>
          <Grid container alignItems={"center"}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  color: "#2362B4",
                  fontSize: { xs: "0.95rem", md: "1.1rem" },
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconButton
                  onClick={() => {
                    setStockOpen(!stockOpen);
                  }}
                >
                  {stockOpen ? (
                    <KeyboardArrowUpIcon
                      sx={{ fontSize: { xs: "1.3rem", md: "1.7rem" }, color: "#2362B4" }}
                    />
                  ) : (
                    <KeyboardArrowDown
                      sx={{ fontSize: { xs: "1.3rem", md: "1.7rem" }, color: "#2362B4" }}
                    />
                  )}{" "}
                </IconButton>
                Filter
              </Typography>
            </Grid>
            <Grid container item xs={6} justifyContent={"flex-end"}>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#2362B4", color: "white" }}
                onClick={getStockData}
              >
                Search
              </Button>
            </Grid>
          </Grid>
          <Collapse in={stockOpen} timeout={"auto"} unmountOnExit>
            <Grid container spacing={"1.5rem"}>
              <Grid
                container
                item
                xs={12}
                md={3}
                spacing={"1rem"}
                alignItems={"center"}
              >
                <Grid item xs={12} justifyContent={"flex-end"}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontWeight: 600,
                      color: "#656565",
                    }}
                  >
                    Date Range
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoItem>
                      <DesktopDatePicker
                        label="Date From"
                        format="DD/MM/YYYY"
                        value={dayjs(stockDateFrom)}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                        onChange={(newValue) => {
                          setStockDateFrom(newValue);
                          if (newValue !== null) {
                            if (`${newValue.$d}` !== "Invalid Date") {
                              const dateFrom = moment(newValue.$d).format(
                                "YYYY-MM-DDTHH:mm:ss"
                              );
                              setStockDateFrom(dateFrom);
                            }
                          }
                        }}
                      />
                    </DemoItem>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoItem>
                      <DesktopDatePicker
                        label="Date To"
                        format="DD/MM/YYYY"
                        value={dayjs(stockDateTo)}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                        onChange={(newValue) => {
                          setStockDateTo(newValue);
                          if (newValue !== null) {
                            if (`${newValue.$d}` !== "Invalid Date") {
                              const dateTo = moment(newValue.$d).format(
                                "YYYY-MM-DDTHH:mm:ss"
                              );
                              setStockDateTo(dateTo);
                            }
                          }
                        }}
                      />
                    </DemoItem>
                  </LocalizationProvider>
                </Grid>
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={3}
              >
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontWeight: 600,
                      color: "#656565",
                      mb: "1rem",
                    }}
                  >
                    Part
                  </Typography>
                  <Autocomplete
                    multiple
                    fullWidth
                    options={partOptions}
                    value={partFilter}
                    getOptionLabel={(option) => option.text || ""}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    onChange={(event, value) => setPartFilter(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Part"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={3}
              >
                <Grid item xs={12}  >
                  <Typography
                    sx={{
                      fontFamily: "Nunito Sans, sans-serif",
                      fontWeight: 600,
                      color: "#656565",
                      mb: "1rem",
                    }}
                  >
                    Stock Room
                  </Typography>

                  <Autocomplete
                    multiple
                    fullWidth
                    options={stockRoomOptions}
                    value={stockRoomFilter}
                    getOptionLabel={(option) => option.text || ""}
                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                    onChange={(event, value) => setStockRoomFilter(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Stock Room"
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      </Grid>

      {isStockLoading ? (
        <Loading />
      ) : (
        <Grid item xs={12}>
          <Paper sx={{ p: "1rem" }}>
            <Typography
              sx={{
                fontSize: { xs: "1rem", md: color.masterPageTitleFontSize },
                fontWeight: color.masterPageTitleFontWeight,
                color: "#2362B4",
              }}
            >
              Stock Room Turn Over Reports
            </Typography>
            <Divider
              sx={{ border: "1px solid rgb(0 0 0 / 17%)", mt: "15px", mb: 2 }}
            />
            {issueData.map((dataList, i) => (
              <div key={i}>
                {i > 0 && (
                  <Divider
                    sx={{ border: "1px solid rgb(0 0 0 / 17%)", my: "15px" }}
                  />
                )}

                <div style={{ display: "flex", alignItems: "center" }}>
                  <IconButton size="small" onClick={() => toggleExpand(i)}>
                    {expandedIndex === i ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>

                  <Typography
                    sx={{
                      color: color.masterDialogTitleColor,
                      fontSize: { xs: "0.9rem", md: color.masterDialogFontSize },
                    }}
                  >
                    {dataList.code + " - " + dataList.partName}
                  </Typography>
                </div>

                <Collapse in={expandedIndex === i} timeout="auto" unmountOnExit>
                  <NormalTable
                    data={dataList.workOrdersList}
                    columns={columnsIssue}
                  />
                </Collapse>
              </div>
            ))}
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default StockRoomReport;
